import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Users, UserCheck, Clock, Hand } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  appsAdvApiRouterGetRsvpList,
  appsAdvApiRouterListWaitlist,
  appsAdvApiRouterListApprovalRequests,
  appsAdvApiRouterRemoveFromWaitlist,
  appsAdvApiRouterApproveApprovalRequest,
  appsAdvApiRouterDeclineApprovalRequest,
  RideOut,
  RSVPOut,
  WaitlistEntryOut,
  ApprovalRequestOut,
} from '@/lib/api';
import { RiderCount } from './RiderCount';
import { ConfirmedList } from './ConfirmedList';
import { WaitlistList } from './WaitlistList';
import { ApprovalList } from './ApprovalList';

interface OrganizerRsvpSectionProps {
  ride: RideOut;
}

interface RsvpListData {
  ride_id: string;
  ride_title: string;
  total_rsvps: number;
  rsvps: RSVPOut[];
}

interface WaitlistListData {
  ride_id: string;
  total_waitlisted: number;
  entries: WaitlistEntryOut[];
}

interface ApprovalListData {
  ride_id: string;
  total_pending: number;
  requests: ApprovalRequestOut[];
}

export function OrganizerRsvpSection({ ride }: OrganizerRsvpSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: rsvpData, isLoading: rsvpLoading } = useQuery({
    queryKey: ['ride-rsvps', ride.id],
    queryFn: async () => {
      const response = await appsAdvApiRouterGetRsvpList(ride.id);
      return response.data as RsvpListData;
    },
  });

  const { data: waitlistData, isLoading: waitlistLoading } = useQuery({
    queryKey: ['ride-waitlist', ride.id],
    queryFn: async () => {
      const response = await appsAdvApiRouterListWaitlist(ride.id);
      return response.data as WaitlistListData;
    },
  });

  const { data: approvalData, isLoading: approvalLoading } = useQuery({
    queryKey: ['ride-approvals', ride.id],
    queryFn: async () => {
      const response = await appsAdvApiRouterListApprovalRequests(ride.id);
      return response.data as ApprovalListData;
    },
    enabled: ride.requires_approval,
  });

  const confirmedCount = rsvpData?.total_rsvps ?? 0;
  const waitlistCount = waitlistData?.total_waitlisted ?? 0;
  const approvalCount = approvalData?.total_pending ?? 0;
  const isLoading = rsvpLoading || waitlistLoading || (ride.requires_approval && approvalLoading);
  const isFull = ride.max_participants != null && (ride.rsvp_count ?? 0) >= ride.max_participants;

  const handleRemoveFromWaitlist = async (entryId: string) => {
    try {
      await appsAdvApiRouterRemoveFromWaitlist(ride.id, entryId);
      toast({
        title: 'Removed from waitlist',
        description: 'User has been removed from the waitlist.',
      });
      queryClient.invalidateQueries({ queryKey: ['ride-waitlist', ride.id] });
    } catch (error: any) {
      toast({
        title: 'Failed to remove',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      const response = await appsAdvApiRouterApproveApprovalRequest(ride.id, requestId);
      toast({
        title: 'Request approved',
        description: response.data.message,
      });
      queryClient.invalidateQueries({ queryKey: ['ride-approvals', ride.id] });
      queryClient.invalidateQueries({ queryKey: ['ride-rsvps', ride.id] });
      queryClient.invalidateQueries({ queryKey: ['ride-waitlist', ride.id] });
    } catch (error: any) {
      toast({
        title: 'Failed to approve',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDecline = async (requestId: string) => {
    try {
      const response = await appsAdvApiRouterDeclineApprovalRequest(ride.id, requestId);
      toast({
        title: 'Request declined',
        description: response.data.message,
      });
      queryClient.invalidateQueries({ queryKey: ['ride-approvals', ride.id] });
    } catch (error: any) {
      toast({
        title: 'Failed to decline',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="glass-card rounded-lg p-6">
        <div className="flex items-center gap-2">
          <Loader2 className="animate-spin text-adv-orange" />
          <span className="text-adv-lightGray">Loading attendee data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4 flex items-center">
        <Users size={20} className="mr-2 text-adv-orange" />
        Rider Management
      </h2>

      <div className="mb-4">
        <RiderCount
          count={confirmedCount}
          maxParticipants={ride.max_participants ?? undefined}
        />
        {ride.requires_approval && approvalCount > 0 && (
          <p className="text-sm text-adv-lightGray mt-1">
            {approvalCount} pending approval
          </p>
        )}
      </div>

      <Tabs defaultValue="confirmed" className="w-full">
        <TabsList className={`grid w-full ${ride.requires_approval ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <TabsTrigger value="confirmed" className="flex items-center justify-center gap-1.5">
            <UserCheck size={16} />
            <Badge variant="secondary" className="text-xs">{confirmedCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="waitlist" className="flex items-center justify-center gap-1.5">
            <Clock size={16} />
            <Badge variant="secondary" className="text-xs">{waitlistCount}</Badge>
          </TabsTrigger>
          {ride.requires_approval && (
            <TabsTrigger value="approvals" className="flex items-center justify-center gap-1.5">
              <Hand size={16} />
              <Badge variant="secondary" className="text-xs">{approvalCount}</Badge>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="confirmed" className="mt-4">
          <ConfirmedList rsvps={rsvpData?.rsvps ?? []} />
        </TabsContent>

        <TabsContent value="waitlist" className="mt-4">
          <WaitlistList
            entries={waitlistData?.entries ?? []}
            onRemove={handleRemoveFromWaitlist}
          />
        </TabsContent>

        {ride.requires_approval && (
          <TabsContent value="approvals" className="mt-4">
            <ApprovalList
              requests={approvalData?.requests ?? []}
              isFull={isFull}
              onApprove={handleApprove}
              onDecline={handleDecline}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
